import { useEffect, type ReactNode } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import {
  type QuotePackage,
  type PricingPlan,
  BASE_PLAN_INCLUSIONS,
  adjustmentLineItems,
} from "./helpers";
import { formatCurrency } from "helpers/currency";
import CallButton from "components/CallButton";

/**
 * Line item row for price breakdown
 */
function LineItem({
  label,
  value,
  subtext,
  bold,
}: {
  label: string;
  value: ReactNode;
  subtext?: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`text-md flex items-start justify-between gap-2.5 leading-relaxed text-evergreen ${bold ? "font-bold" : ""}`}
    >
      <p className="flex-1">
        {label}
        {subtext && (
          <span className="font-normal italic opacity-80"> {subtext}</span>
        )}
      </p>
      <p className="flex-1 text-right">{value}</p>
    </div>
  );
}

/**
 * Price Breakdown Modal
 * Full screen overlay with price breakdown details
 */
export default function PriceBreakdownModal({
  pkg,
  plan,
  isOpen,
  onClose,
}: {
  pkg: QuotePackage;
  plan?: PricingPlan;
  isOpen: boolean;
  onClose: () => void;
}) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-ivory">
      {/* Header with close button */}
      <div className="flex h-[60px] items-center">
        <button
          onClick={onClose}
          className="flex items-center justify-center"
          aria-label="Close"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 pb-4">
        <div className="space-y-4">
          {/* Base inclusions that come with all plans */}
          {BASE_PLAN_INCLUSIONS.map((item, index) => (
            <LineItem
              key={`base-${index}`}
              label={item.name}
              subtext={item.subtext}
              value="Included"
            />
          ))}
          {/* Plan-specific highlights from API */}
          {plan?.external?.inclusion_highlights?.map((item, index) => (
            <LineItem
              key={`highlight-${index}`}
              label={item.title}
              value="Included"
            />
          ))}

          {/* Package */}
          {pkg.breakdown.package_price > 0 && (
            <LineItem
              label="Package"
              value={formatCurrency(pkg.breakdown.package_price)}
            />
          )}
          {/* Death Certificates - show as "X @ $Y" format */}
          {pkg.breakdown.death_certificates?.total_fee > 0 && (
            <LineItem
              label="Death Certificates"
              value={`${pkg.breakdown.death_certificates.total_quantity} @ ${formatCurrency(
                Math.round(
                  pkg.breakdown.death_certificates.total_fee /
                    pkg.breakdown.death_certificates.total_quantity,
                ),
              )}`}
            />
          )}
          {pkg.breakdown.weight_fee?.total_fee > 0 && (
            <LineItem
              label="Weight fee"
              value={formatCurrency(pkg.breakdown.weight_fee.total_fee)}
            />
          )}
          {pkg.breakdown.transport_fee?.total_fee > 0 && (
            <LineItem
              label="Transportation"
              value={formatCurrency(pkg.breakdown.transport_fee.total_fee)}
            />
          )}
          {pkg.breakdown.delivery_fee?.total_fee > 0 && (
            <LineItem
              label="Delivery"
              value={formatCurrency(pkg.breakdown.delivery_fee.total_fee)}
            />
          )}
          {pkg.breakdown.pacemaker_fee?.total_fee > 0 && (
            <LineItem
              label="Pacemaker removal"
              value={formatCurrency(pkg.breakdown.pacemaker_fee.total_fee)}
            />
          )}
          {pkg.breakdown.fingerprint_fee?.total_fee > 0 && (
            <LineItem
              label="Fingerprints"
              value={formatCurrency(pkg.breakdown.fingerprint_fee.total_fee)}
            />
          )}
          {pkg.breakdown.inurnment_fee?.total_fee > 0 && (
            <LineItem
              label="Inurnment"
              value={`${pkg.breakdown.inurnment_fee.total_quantity} @ ${formatCurrency(pkg.breakdown.inurnment_fee.unit_price)}`}
            />
          )}
          {pkg.breakdown.storage &&
            pkg.breakdown.storage.total_fee > 0 &&
            pkg.breakdown.storage.total_quantity > 0 && (
              <LineItem
                label="Storage"
                value={`${pkg.breakdown.storage.total_quantity} days @ ${formatCurrency(
                  Math.round(
                    pkg.breakdown.storage.total_fee /
                      pkg.breakdown.storage.total_quantity,
                  ),
                )}`}
              />
            )}
          {pkg.breakdown.state_fees?.map((fee, index) => (
            <LineItem
              key={`state-${index}`}
              label={fee.name}
              value={formatCurrency(fee.amount)}
            />
          ))}
          {pkg.breakdown.county_fees?.map((fee, index) => (
            <LineItem
              key={`county-${index}`}
              label={fee.name}
              value={formatCurrency(fee.amount)}
            />
          ))}
          {pkg.breakdown.additional_services
            ?.filter(
              (s) => (s.total_quantity ?? 0) > 0 || (s.total_fee ?? 0) > 0,
            )
            .map((service, index) => (
              <LineItem
                key={`addon-${index}`}
                label={service.label || service.name}
                value={
                  (service.total_quantity ?? 0) > 1
                    ? `${service.total_quantity} @ ${formatCurrency(service.amount)}`
                    : formatCurrency(service.total_fee ?? service.amount)
                }
              />
            ))}
          {adjustmentLineItems(pkg.breakdown).map((item) => (
            <LineItem
              key={item.key}
              label={item.label}
              value={item.displayValue}
            />
          ))}

          {/* Total */}
          <LineItem label="Total" value={formatCurrency(pkg.total)} bold />
        </div>
      </div>

      {/* CTA at bottom */}
      <div className="px-3 pb-4">
        <CallButton variant="primary" />
      </div>
    </div>
  );
}
